/**
 * StructuresPage — /structures (V9.13 Sprint 2 UX)
 *
 * Hub visuel qui regroupe les 3 annuaires (Clubs / Coachs / Fédérations)
 * en réutilisant leurs endpoints existants (pas de duplication).
 * Les URLs `/clubs`, `/coachs`, `/federations` restent inchangées.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Crown, Users, ArrowRight, Shield } from "lucide-react";

function StructureCard({ to, icon: Icon, iconColor, iconBg, label, description, count, testid }) {
  return (
    <Link to={to} data-testid={testid} className="block group">
      <Card className="p-5 h-full transition-all hover:border-current hover:shadow-lg" style={{ borderColor: "transparent" }}>
        <div className="flex items-start gap-4">
          <div
            className="h-14 w-14 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: iconBg, color: iconColor }}
          >
            <Icon className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-bold text-lg">{label}</h2>
              {count !== null && count !== undefined && (
                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground" data-testid={`${testid}-count`}>
                  {count}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 leading-snug">{description}</p>
            <div className="mt-3 text-sm font-medium inline-flex items-center gap-1 text-[hsl(var(--aito-lagoon))] group-hover:gap-1.5 transition-all">
              Explorer <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function StructuresPage() {
  const [counts, setCounts] = useState({ clubs: null, coaches: null, federations: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, co, f] = await Promise.all([
          api.get("/clubs", { params: { limit: 1 } }).catch(() => ({ data: { count: 0 } })),
          api.get("/coaches", { params: { limit: 1 } }).catch(() => ({ data: { count: 0 } })),
          api.get("/federations", { params: { limit: 1 } }).catch(() => ({ data: { count: 0 } })),
        ]);
        if (!cancelled) {
          setCounts({
            clubs: c.data?.count ?? 0,
            coaches: co.data?.count ?? 0,
            federations: f.data?.count ?? 0,
          });
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-5xl mx-auto py-6 md:py-10 px-4 space-y-6" data-testid="structures-page">
      <header className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground bg-muted/50 mb-3">
          <Shield className="h-3.5 w-3.5" />Structures sportives polynésiennes
        </div>
        <h1 className="font-display font-bold text-3xl md:text-4xl leading-tight">
          Clubs, Coachs & Fédérations
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Toutes les structures qui font vivre le sport en Polynésie française. Accède à leur annuaire, leur palmarès et leurs membres validés.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 w-full" />)
        ) : (
          <>
            <StructureCard
              to="/clubs"
              icon={Building2}
              iconColor="hsl(var(--aito-lagoon))"
              iconBg="hsl(var(--aito-lagoon) / 0.10)"
              label="Clubs"
              description="Les clubs sportifs de toutes les îles : équipes, palmarès, coachs et athlètes affiliés."
              count={counts.clubs}
              testid="structures-card-clubs"
            />
            <StructureCard
              to="/coachs"
              icon={Users}
              iconColor="hsl(var(--aito-lagoon))"
              iconBg="hsl(var(--aito-lagoon) / 0.10)"
              label="Coachs"
              description="Entraîneurs, préparateurs et référents techniques qui accompagnent nos athlètes."
              count={counts.coaches}
              testid="structures-card-coaches"
            />
            <StructureCard
              to="/federations"
              icon={Crown}
              iconColor="hsl(var(--aito-gold))"
              iconBg="hsl(var(--aito-gold) / 0.12)"
              label="Fédérations"
              description="Les fédérations sportives territoriales, leurs clubs de tutelle et leurs disciplines."
              count={counts.federations}
              testid="structures-card-federations"
            />
          </>
        )}
      </div>
    </div>
  );
}
